import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    ImageColumn,
    VehicleImage,
    ArrowButton,
    ExpandButton,
    FullscreenOverlay,
    FullscreenCloseButton,
    FullscreenImage,
    FullscreenArrowButton,
    FullscreenCounter,
    ThumbnailStrip,
    Thumbnail,
    PreviewImageWrapper
} from './ImageCarousel.style';
import { FALLBACK_SVG } from '../../constants';
import { ArrowDirection } from '../../enums';
import { ExpandIcon } from '../icons/ExpandIcon';
import type { ImageCarouselProps } from '../../types/VehicleSectionProps';

/**
 * ImageCarousel: Displays a carousel of images with navigation, thumbnail strip,
 * and a fullscreen preview mode.
 */
export const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, vehicleImageAlt }) => {
    const hasMultiple = images.length > 1;
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isFallbackImage = images[activeIndex] === FALLBACK_SVG;
    const thumbnailStripRef = useRef<HTMLDivElement>(null);

    const handlePrev = useCallback(
        () => setActiveIndex((currentIndex) => (currentIndex === 0 ? images.length - 1 : currentIndex - 1)),
        [images.length]
    );
    const handleNext = useCallback(
        () => setActiveIndex((currentIndex) => (currentIndex === images.length - 1 ? 0 : currentIndex + 1)),
        [images.length]
    );

    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        (e.target as HTMLImageElement).src = FALLBACK_SVG;
    }, []);

    const openFullscreen = useCallback(() => setIsFullscreen(true), []);
    const closeFullscreen = useCallback(() => setIsFullscreen(false), []);

    useEffect(() => {
        const el = thumbnailStripRef.current;
        if (!el) return undefined;

        const handleWheel = (e: WheelEvent) => {
            if (e.deltaX === 0 && e.deltaY === 0) return;
            if (el.scrollWidth <= el.clientWidth) return;
            e.preventDefault();
            el.scrollLeft += e.deltaX !== 0 ? e.deltaX : e.deltaY;
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [hasMultiple]);

    // Close fullscreen on Escape key
    useEffect(() => {
        if (!isFullscreen) return undefined;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeFullscreen();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen, closeFullscreen, handlePrev, handleNext]);

    return (
        <>
            {/* Main image area */}
            <ImageColumn>
                {/* Active image */}
                <VehicleImage
                    src={images[activeIndex]}
                    alt={`${vehicleImageAlt} – photo ${activeIndex + 1}`}
                    data-testid="vehicle-image"
                    onError={handleImageError}
                />
                {/* Expand is hidden for the blank placeholder image */}
                {!isFallbackImage && (
                    <ExpandButton onClick={openFullscreen} data-testid="expand-button" aria-label="Expand image">
                        <ExpandIcon />
                    </ExpandButton>
                )}

                {/* Carousel controls — only shown when multiple images available */}
                {hasMultiple && (
                    <>
                        <ArrowButton
                            direction={ArrowDirection.Prev}
                            onClick={handlePrev}
                            data-testid="carousel-prev"
                            aria-label="Previous photo"
                        >
                            ‹
                        </ArrowButton>
                        <ArrowButton
                            direction={ArrowDirection.Next}
                            onClick={handleNext}
                            data-testid="carousel-next"
                            aria-label="Next photo"
                        >
                            ›
                        </ArrowButton>
                    </>
                )}
            </ImageColumn>

            {/* Thumbnail strip — only shown when multiple images available */}
            {hasMultiple && (
                <ThumbnailStrip ref={thumbnailStripRef} data-testid="thumbnail-strip">
                    {images.map((src, index) => (
                        <Thumbnail
                            key={src}
                            src={src}
                            alt={`Thumbnail ${index + 1}`}
                            $active={index === activeIndex}
                            onClick={() => setActiveIndex(index)}
                            onError={handleImageError}
                            data-testid={`thumbnail-${index}`}
                        />
                    ))}
                </ThumbnailStrip>
            )}
            {isFullscreen && (
                <FullscreenOverlay data-testid="fullscreen-overlay" onClick={closeFullscreen}>
                    <PreviewImageWrapper>
                        <FullscreenCloseButton onClick={closeFullscreen} data-testid="fullscreen-close" aria-label="Close preview">
                            ×
                        </FullscreenCloseButton>

                        <FullscreenImage
                            src={images[activeIndex]}
                            alt={`${vehicleImageAlt} – photo ${activeIndex + 1}`}
                            data-testid="fullscreen-image"
                            onClick={(e) => e.stopPropagation()}
                            onError={handleImageError}
                        />

                        {hasMultiple && (
                            <>
                                <FullscreenArrowButton
                                    direction={ArrowDirection.Prev}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePrev();
                                    }}
                                    data-testid="fullscreen-prev"
                                    aria-label="Previous photo"
                                >
                                    ‹
                                </FullscreenArrowButton>
                                <FullscreenArrowButton
                                    direction={ArrowDirection.Next}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNext();
                                    }}
                                    data-testid="fullscreen-next"
                                    aria-label="Next photo"
                                >
                                    ›
                                </FullscreenArrowButton>
                                <FullscreenCounter data-testid="fullscreen-counter">
                                    {`(${activeIndex + 1} / ${images.length})`}
                                </FullscreenCounter>
                            </>
                        )}
                    </PreviewImageWrapper>
                </FullscreenOverlay>
            )}
        </>
    );
};
